import PropTypes from 'prop-types'
import React, { Fragment } from 'react'
import cn from 'classnames'
import Label from './Label'
import { InputStyle } from './InputStyle'
import Icon from './../../Icon/Icon'
import LinkWrapper from './../../LinkWrapper/LinkWrapper'
import formatPartialReplacement from '../../../utility/formatPartialReplacement'
import FieldError from '../FieldError'

const SIZES = ['xs', 'sm', 'md', 'lg']

export default class Input extends React.Component {
  static propTypes = {
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    value: PropTypes.any,
    label: PropTypes.string,
    labelStyle: PropTypes.string,
    placeholder: PropTypes.string,
    isValid: PropTypes.bool,
    type: PropTypes.string,
    error: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool
    ]),
    className: PropTypes.string,
    suffixAddon: PropTypes.string,
    leftItem: PropTypes.node,
    rightItem: PropTypes.node,
    size: PropTypes.oneOf(SIZES),
    autoFocus: PropTypes.bool,
    helpText: PropTypes.string,
    helpTextTools: PropTypes.object,
    maskRegExp: PropTypes.string,
    maskReplacement: PropTypes.string,
    inputRef: PropTypes.object,
  }

  static defaultProps = {
    type: 'text',
    error: false,
    className: '',
    size: 'lg',
    autoFocus: false
  }

  constructor (props) {
    super(props)
    const value = props.data ?? props.value ?? ''
    this.state = {
      inputText: '' + value,
      hasFocus: false
    }
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    let inputText = prevState.inputText ?? ''
    const nextPropsText = nextProps.data ?? nextProps.value

    if ((!inputText && nextPropsText) || (prevState.propsData !== nextPropsText) || (inputText && !nextPropsText)) {
      inputText = nextPropsText
    }
    let newState = {
      propsData: nextProps.data,
      propsValue: nextProps.value
    }

    let forceFormat = false
    if (nextProps.maskRegExp) {
      const isString = typeof nextProps.maskRegExp === 'string'
      const source = isString ? nextProps.maskRegExp : nextProps.maskRegExp?.source
      if (source !== prevState?.maskRegExp?.source) {
        newState.maskRegExp = new RegExp(source)
        forceFormat = true
      }
    }
    if (nextProps.validRegExp) {
      const isString = typeof nextProps.validRegExp === 'string'
      const source = isString ? nextProps.validRegExp : nextProps.validRegExp?.source
      if (source !== prevState?.validRegExp?.source) {
        newState.validRegExp = new RegExp(source)
      }
    }
    if (prevState.inputText !== inputText || forceFormat) {
      const re = newState.maskRegExp || prevState.maskRegExp
      if (re) {
        re.lastIndex = 0
        const res = re.exec(inputText)
        if (res) {
          inputText = formatPartialReplacement(nextProps.maskReplacement, res, true)
        }
      }
      newState.inputText = inputText
    }

    return !!(Object.keys(newState).length) ? newState : null
  }

  onChange = (e) => {
    const { onChange, onValueChange, maskReplacement } = this.props
    let inputText = e.target.value

    if (this.state.maskRegExp) {
      let re = this.state.maskRegExp
      re.lastIndex = 0
      const res = re.exec(e.target.value.replaceAll(',',''))
      if (res) {
        inputText = formatPartialReplacement(maskReplacement, res, false)
        let valid = this.state.validRegExp?.test(inputText)
        this.setState({ inputText, valid })
        onChange?.({target: { value: inputText }})
        onValueChange?.(inputText)
      }
    } else if (inputText !== this.state.inputText) {
      let valid = this.state.validRegExp?.test(inputText)
      this.setState({ inputText, valid })
      onChange?.({target: { value: inputText }})
      onValueChange?.(inputText)
    }
  }

  onKeyDown = (e) => {
    this.props.onKeyDown?.(e)
  }

  onFocus = (e) => {
    const { maskReplacement, onFocus } = this.props

		if (maskReplacement && e.target.value === '') {
			const inputText = formatPartialReplacement(maskReplacement, false, false)
			this.setState({ inputText, hasFocus: true })
		} else {
			this.setState({ hasFocus: true })
    }

    onFocus?.()
  }

  onBlur = (e) => {
    const { maskReplacement, onBlur } = this.props
    const { maskRegExp } = this.state

		if (!!this.state.inputText && maskRegExp) {
			const res = maskRegExp.exec(e.target.value)
			const inputText = formatPartialReplacement(maskReplacement, res, true)
			this.setState({ inputText, hasFocus: false })
		} else {
      this.setState({ hasFocus: false })
    }

    onBlur?.(e.target.value)
  }

  render () {
    const {
      label,
      type,
      error,
      isValid,
      required,
      className,
      size,
      suffixAddon,
      style,
      leftItem,
      rightItem,
      inputRef,
      helpText,
      helpTextTools,
      placeholder,
      rows,
      icon,
      linkType,
      onIconClick,
      disabled,
      autoFocus,
      config,
      config: {
        charCount,
        charMaxCount,
        wordCount,
        wordsMaxCount
      } = {},
      words,
      ...props
    } = this.props
    let valid = (isValid != null) ? isValid : this.state.valid
    const { inputText } = this.state
    const errorMessage = error && typeof error === 'string' ? error : null
    const styleObj = type === 'number' ? { paddingRight: icon ? '1.5rem' :  '.5rem', ...style } : style
    const inputClass = cn(
      className,
      `form-control`,
      size,
      {
        'is-invalid': error || (required && !inputText),
        'is-valid': valid
      }
    )
    let inputComponent
    const maxLength = (charCount && charMaxCount) ||
      (wordCount && wordsMaxCount && words >= wordsMaxCount && (/\s+$/.test(inputText)) && inputText.length) ||
      524288
    const shouldCount = (charCount && charMaxCount) || (wordCount && wordsMaxCount)

    if (type === 'textarea') {
      const { labelStyle, maxCountReached, ...rest } = props
      inputComponent = <Fragment>
        <textarea
          {...rest}
          {...(shouldCount && { maxLength })}
          ref={inputRef}
          value={inputText}
          placeholder={placeholder}
          className={cn(
            className,
            `form-control`,
            `textarea`,
            size,
            {
              'is-invalid': error || (required && !inputText),
              'is-valid': valid
            }
          )}
          required={props.required}
          onChange={this.onChange}
          onKeyDown={props.onKeyDown}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          rows={rows || 1}
          autoFocus={autoFocus}
        />
        <style jsx>{`
          .textarea {
            resize: none;
            height: ${rows > 1 ? `auto` : `calc(1.75rem + 2px)`} !important;
            padding: .187em .375rem .375rem;
          }
        `}</style>
      </Fragment>
    }   else {
      inputComponent = <input
        ref={inputRef}
        value={inputText}
        placeholder={placeholder}
        type={type}
        style={styleObj}
        onClick={props.onClick}
        onChange={this.onChange}
        onKeyDown={props.onKeyDown}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        className={inputClass}
        step={props.step}
        max={props.max}
        min={props.min}
        disabled={disabled}
        autoFocus={autoFocus}
        {...(shouldCount && { maxLength })}
      />
    }
    return (
      <div className='input-wrapper position-relative w-100'>
        <Label {...this.props} />
        <div className={`position-relative w-100`}>
          {
            leftItem &&
            <div className='left'>{leftItem}</div>
          }
          {inputComponent}
          {
            icon && linkType &&
            <span className={`icon-container`}>
              <LinkWrapper link={inputText} type={linkType} target={`_blank`}>
                <Icon
                  icon={icon}
                  styling={{
                    iconColor: '#cccccc'
                  }}
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    lineHeight: '.1rem'
                  }}
                />
              </LinkWrapper>
            </span>
          }
          {
            icon && !linkType &&
            <span className={`icon-container`}>
              <Icon
                icon={icon}
                onIconClick={!!onIconClick && onIconClick}
                styling={{
                  iconColor: '#cccccc'
                }}
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  lineHeight: '.1rem'
                }}
              />
            </span>
          }
          {
            rightItem &&
            <div className='right'>{rightItem}</div>
          }
        </div>
        {suffixAddon ? <span className='suffix'>{suffixAddon}</span> : null}
        <style jsx>{InputStyle}</style>
        <style jsx>{`
          div :global(.form-control) {
            padding: ${inputText && icon ? `.375rem 2rem .375rem .375rem` : `var(--inputPadding)`};
          }
          div :global(.form-control.is-invalid) {
            background-image: none;
          }
          div :global(.form-control:focus + span > i) {
            color: #666666;
          }
          div :global(.form-control:focus + span > i:hover) {
            color: #000;
          }
          div .icon-container {
            position: absolute;
            top: 50%;
            right: .15rem;
            cursor: pointer;
            transform: translateY(-45%);
          }
          .right {
            position: absolute;
            right: ${(icon && '1.5rem') || '.5rem'};
            top: 50%;
            transform: translateY(-50%);
          }
        `}</style>
        <FieldError error={errorMessage} />
      </div>
    )
  }
}
